import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
// Customer Page

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";

// Custom
import CustomToast from "../../components/toast/CustomToast";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomSearch from "../../components/search/Search";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { formatMeterSerialNumber } from "../../utils/helper";

import {
  Grid2 as Grid,
  TableCell,
  TableRow,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CustomDataTable from "../table/CustomTable";
import CreateConsumerModel from "../modals/CreateConsumerModel";
import EditConsumerModel from "../modals/EditConsumerModel";
import { useAppDispatch } from "../../redux/hooks";
import { ToastState, MeterV2T } from "../../types";
import Icon360 from "../../assets/svg/360";
import Chip from "@mui/material/Chip";
import {
  setCustomerDetails,
  setCustomerId,
} from "../../redux/features/CustomerSlice";
import {
  setIsMeterLoading,
  setMeterDetails,
  setHasMeterSearched,
  setMeterId,
} from "../../redux/features/MeterSlice";
import AddCustomFieldsDrawer from "../drawer/AddCustomFieldsDrawer";
import Swal from "sweetalert2";

const COLUMNS = [
  "Index",
  "Meter Id",
  "Meter Number",
  "Site Name",
  "Meter Type",
  "Installation Date",
  "Connectivity",
  "Status",
  "Actions",
];

const MeterMock: React.FC = () => {
  const navigate = useNavigate();
  const [DATA, SetDATA] = useState<MeterV2T[]>([]);

  const dispatch = useAppDispatch();
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPage: 0,
    totalElement: 0,
    pageSize: 10,
  });
  const [openModal, setOpenModal] = useState<{
    type: string;
    id: string;
    onlyMeter: boolean;
  }>({
    type: "",
    id: "",
    onlyMeter: true,
  });
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      // Fetch all data from API since server-side pagination/search isn't supported yet
      const url = `v2/Meters?Page=1&PageSize=1000`;
      console.log("Fetching all meters from:", url);

      const meterResponse = await fetchGETRequest<any>(url, {}, false);
      const transformedArray = meterResponse?.data || [];

      SetDATA(transformedArray);
    } catch (error) {
      console.error("Error fetching meters:", error);
      SetDATA([]);
      setToast({
        message: "Error fetching meters. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Local filtering based on searchValue
  const filteredData = useMemo(() => {
    if (!searchValue) return DATA;
    const lowerSearch = searchValue.toLowerCase();
    return DATA.filter(
      (item) =>
        item.meterNumber?.toLowerCase().includes(lowerSearch) ||
        item.id?.toLowerCase().includes(lowerSearch),
    );
  }, [DATA, searchValue]);

  // Local pagination based on filteredData
  const paginatedData = useMemo(() => {
    const startIndex = pagination.currentPage * pagination.pageSize;
    return filteredData.slice(startIndex, startIndex + pagination.pageSize);
  }, [filteredData, pagination.currentPage, pagination.pageSize]);

  // Update total elements and pages when filtered data changes
  useEffect(() => {
    setPagination((old) => ({
      ...old,
      totalElement: filteredData.length,
      totalPage: Math.ceil(filteredData.length / old.pageSize),
      // Reset to first page if current page is out of bounds
      currentPage:
        old.currentPage >= Math.ceil(filteredData.length / old.pageSize) &&
        filteredData.length > 0
          ? 0
          : old.currentPage,
    }));
  }, [filteredData.length, pagination.pageSize]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleChangeRowsPerPage = (event: any) => {
    setPagination((old) => ({
      ...old,
      pageSize: parseInt(event.target.value, 10),
      currentPage: 0,
    }));
  };

  const handleEditConsumer = (id: string) => {
    setOpenModal({ type: "EDIT", id: id, onlyMeter: true });
  };

  const handleSearchMeter360 = async (id: string) => {
    dispatch(setMeterId(id));
    navigate("/meter-dashboard", { state: id });
  };

  const rowsContent = paginatedData.map((item: MeterV2T, index: number) => (
    <TableRow key={item.id}>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {index + pagination.currentPage * pagination.pageSize + 1}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {item.id || "N/A"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {formatMeterSerialNumber(item.meterNumber) || "N/A"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody">{item.siteName || "N/A"}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody">{item.meterType || "N/A"}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody">
          {item.installationDate
            ? new Date(item.installationDate).toLocaleDateString()
            : "N/A"}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={item.isConnected ? "Connected" : "Disconnected"}
          size="small"
          sx={{
            backgroundColor: item.isConnected ? "#E8F5E9" : "#FFEBEE",
            color: item.isConnected ? "#2E7D32" : "#C62828",
            fontWeight: "bold",
          }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={item.isActive ? "Active" : "Inactive"}
          size="small"
          sx={{
            backgroundColor: item.isActive ? "#E8F5E9" : "#FFEBEE",
            color: item.isActive ? "#2E7D32" : "#C62828",
            fontWeight: "bold",
          }}
        />
      </TableCell>
      <TableCell>
        <IconButton onClick={() => handleEditConsumer(item.id)} title="Edit">
          <EditIcon sx={{ fontSize: "20px" }} />
        </IconButton>
        <IconButton
          onClick={() => handleSearchMeter360(item.id)}
          title="Consumer-360"
        >
          <Icon360 />
        </IconButton>
      </TableCell>
    </TableRow>
  ));

  return (
    <Box>
      <Card
        sx={{
          borderRadius: "12px",
          padding: "20px",
          backgroundColor: "#fff",
          marginBottom: "20px",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: "20px" }}
        >
          <Typography variant="h6" fontWeight="bold">
            Meters
          </Typography>
          <Stack direction="row" spacing={2}>
            <CustomSearch
              placeholder={"Search Meter By Id or Name"}
              sx={{ width: "500px", height: "35px" }}
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setPagination((old) => ({ ...old, currentPage: 0 }));
              }}
            />
            <ContainedButton
              label={"Create meter"}
              sx={{
                width: "140px",
                height: "35px",
                fontSize: "12px",
                padding: "0",
                boxShadow: "0",
              }}
              onClick={() => {
                Swal.fire({
                  title: "Do you want to create only a Meter?",
                  text: "Press No to create a complete Consumer",
                  icon: "question",
                  showCancelButton: true,
                  confirmButtonText: "Yes",
                  cancelButtonText: "No",
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                }).then((result) => {
                  if (result.isConfirmed) {
                    setOpenModal({
                      type: "CREATENEW",
                      id: "",
                      onlyMeter: true,
                    });
                  } else {
                    setOpenModal({
                      type: "CREATENEW",
                      id: "",
                      onlyMeter: false,
                    });
                  }
                });
              }}
            />
          </Stack>
        </Stack>
        <Grid container spacing={2.25}>
          <Grid size={{ xs: 12 }}>
            <Card sx={{ borderRadius: "0px !important" }}>
              <CustomDataTable
                columns={COLUMNS}
                rowsContent={rowsContent}
                isLoading={loading}
                pagination
                Actions={true}
                onPageChange={(_, newPage) =>
                  setPagination((old) => ({ ...old, currentPage: newPage }))
                }
                rowsPerPage={pagination.pageSize}
                rowsPerPageOptions={[10, 50, 100]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                totalCount={pagination.totalElement}
                page={pagination.currentPage}
              />
            </Card>
          </Grid>
        </Grid>
      </Card>

      <AddCustomFieldsDrawer setToast={setToast} />

      <CreateConsumerModel
        openModal={openModal}
        setOpenModal={setOpenModal}
        setToast={setToast}
        addOrEdit={"add"}
        handleCustomerAndAssetSearch={() => {}}
      />

      <EditConsumerModel
        openModal={openModal}
        setOpenModal={(data) => {
          setOpenModal({ id: data.id, type: data.type, onlyMeter: true });
        }}
        setToast={setToast}
      />

      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />
    </Box>
  );
};

export default MeterMock;
